import React, { useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { requestPlanSuggestion, sendAiFeedback } from "../../api/aiIntelligence";

function valueOrEmpty(value) {
  return typeof value === "string" ? value.trim() : "";
}

export default function PlanIntelligenceSuggestion({
  itemType,
  payload,
  onApply,
  applyLabel = "Apply suggestion",
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState("");

  async function handleRequest() {
    setLoading(true);
    setError("");
    try {
      const result = await requestPlanSuggestion(itemType, payload);
      if (result?.locked) {
        setSuggestion(result);
      } else {
        setSuggestion(result || null);
      }
    } catch (err) {
      setError(err?.message || "Unable to generate Insights suggestion.");
    } finally {
      setLoading(false);
    }
  }

  async function handleApply() {
    if (!suggestion?.suggestion) return;
    onApply?.(suggestion.suggestion);
    sendAiFeedback({
      action: "applied",
      taskType: `${itemType}_improvement`,
    }).catch(() => {});
  }

  const structured = suggestion?.suggestion || {};
  const hasStructuredSuggestion = Object.keys(structured).length > 0;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        bgcolor: "#fbfcff",
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Insights
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={handleRequest}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            {loading ? "Improving..." : "Improve with Insights"}
          </Button>
        </Stack>

        {error ? <Alert severity="warning">{error}</Alert> : null}

        {suggestion?.locked ? (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={() => navigate("/trustbud-plus?feature=plan")}>
                See Plus
              </Button>
            }
          >
            {suggestion.detail || "Unlock Insights to improve this plan."}
          </Alert>
        ) : null}

        {suggestion && !suggestion.locked ? (
          <Stack spacing={1}>
            {suggestion.ai_text ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {suggestion.ai_text}
              </Typography>
            ) : null}

            {hasStructuredSuggestion ? (
              <Box sx={{ display: "grid", gap: 0.5 }}>
                {valueOrEmpty(structured.suggested_title) ? (
                  <Typography variant="body2">
                    <strong>Suggested:</strong> {structured.suggested_title}
                  </Typography>
                ) : null}
                {valueOrEmpty(structured.suggested_description) ? (
                  <Typography variant="body2">
                    <strong>Description:</strong> {structured.suggested_description}
                  </Typography>
                ) : null}
                {valueOrEmpty(structured.suggested_timeframe) ? (
                  <Typography variant="body2">
                    <strong>Timeframe:</strong> {structured.suggested_timeframe}
                  </Typography>
                ) : null}
                {valueOrEmpty(structured.backup_version) ? (
                  <Typography variant="body2">
                    <strong>Keep-alive step:</strong> {structured.backup_version}
                  </Typography>
                ) : null}
                {valueOrEmpty(structured.suggested_window) ? (
                  <Typography variant="body2">
                    <strong>Window:</strong> {structured.suggested_window}
                  </Typography>
                ) : null}
                {valueOrEmpty(structured.reason) ? (
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {structured.reason}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            {hasStructuredSuggestion ? (
              <Button
                size="small"
                variant="contained"
                onClick={handleApply}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                {applyLabel}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
